
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  VorankuendigungData,
  VorankuendigungCoordinator,
  VorankuendigungManualField,
  VorankuendigungParty,
  VorankuendigungPerson,
  ReportMetadata,
  AppSettings,
  Authority,
} from '../types';
import { DANGEROUS_WORKS, PROJECT_TYPES } from '../data/constants';
import { Search, MapPin, User, Info, X, Copy, Loader2, ArrowDownCircle, ShieldCheck, ChevronDown, Plus, Trash2, RotateCcw, Lock } from 'lucide-react';
import { getFieldExplanation } from '../services/geminiService';
import { fetchAuthorities } from '../services/cloudService';
import {
  clearManualField,
  emptyCoordinator,
  emptyParty,
  emptyPerson,
  isManualField,
  markManualField,
  normalizeVorankuendigung,
} from '../services/vorankuendigungModel';

interface Props {
  data: VorankuendigungData;
  onChange: (data: VorankuendigungData) => void;
  metadata?: ReportMetadata;
  settings?: AppSettings;
}

/**
 * Textfeld, das mit seinem Inhalt wächst.
 *
 * Die Vorankündigung nimmt in fast jedem Feld mehr auf, als eine feste
 * Zeilenzahl zeigt (mehrere Personen, lange Anschriften). Ein fester `rows`-Wert
 * versteckt genau das, was der Anwender gerade eingegeben hat.
 */
const AutoTextArea = ({
  value,
  onChange,
  minRows = 3,
  placeholder = '',
  className = '',
}: { value: string; onChange: (v: string) => void; minRows?: number; placeholder?: string; className?: string }) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      className={`w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm resize-y overflow-hidden ${className}`}
      rows={minRows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
};

const TextArea = AutoTextArea;

const TextInput = ({ value, onChange, placeholder = "", className = "" }: { value: string, onChange: (v: string) => void, placeholder?: string, className?: string }) => (
  <input
    type="text"
    className={`w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm ${className}`}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
  />
);

interface InputGroupProps {
  label: string;
  helpQuery?: string;
  loadingHelp: string | null;
  onExplain: (title: string, query: string) => void;
  action?: React.ReactNode;
}

const InputGroup = ({
  label,
  helpQuery,
  loadingHelp,
  onExplain,
  action,
  children
}: React.PropsWithChildren<InputGroupProps>) => (
  <div className="mb-4">
    <div className="flex items-center gap-2 mb-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {helpQuery && (
        <button
          onClick={() => onExplain(label, helpQuery)}
          className="text-blue-600 hover:text-blue-800 transition-colors"
          title="KI-Erklärung anzeigen"
        >
          {loadingHelp === label ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
        </button>
      )}
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </div>
);

/** Rollen-Vorschläge für alle Personenlisten (Punkt 2, 4 und 5). */
const ROLE_SUGGESTIONS = ['Vertreter', '2. SiGeKo', 'Ansprechpartner', 'Bauleitung', 'Stellvertretung'];

const ROLE_DATALIST_ID = 'vorank-role-suggestions';

const RoleDatalist = () => (
  <datalist id={ROLE_DATALIST_ID}>
    {ROLE_SUGGESTIONS.map(role => <option key={role} value={role} />)}
  </datalist>
);

const EntryCard = ({ title, onRemove, badge, children }: React.PropsWithChildren<{ title: string; onRemove?: () => void; badge?: React.ReactNode }>) => (
  <div className="relative p-3 rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{title}</span>
      <div className="flex items-center gap-2">
        {badge}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-gray-400 hover:text-red-600 transition-colors"
            title="Eintrag entfernen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
    {children}
  </div>
);

const AddEntryButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 px-3 py-1.5 rounded-lg transition"
  >
    <Plus className="w-3.5 h-3.5" /> {label}
  </button>
);

const MultiPersonHint = ({ text }: { text: string }) => (
  <div className="mb-2 text-[11px] text-slate-500 flex items-start gap-1">
    <Info className="w-3 h-3 mt-0.5 shrink-0" />
    <span>{text}</span>
  </div>
);

/** Kennzeichnung + Rückweg für Felder, die sonst aus den Projektdaten kämen. */
const ManualBadge = ({ manual, canReset, onRelease }: { manual: boolean; canReset: boolean; onRelease: () => void }) => {
  if (!manual) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
        <Lock className="w-3 h-3" /> Manuell
      </span>
      {canReset && (
        <button
          type="button"
          onClick={onRelease}
          className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800"
          title="Wieder aus den Projektdaten füllen lassen"
        >
          <RotateCcw className="w-3 h-3" /> Projektdaten
        </button>
      )}
    </div>
  );
};

// ── Punkt 5: Koordinatorenliste ────────────────────────────────────────────

interface CoordinatorListProps {
  label: string;
  helpContext: string;
  list: VorankuendigungCoordinator[];
  onChange: (list: VorankuendigungCoordinator[]) => void;
  loadingHelp: string | null;
  onExplain: (t: string, q: string) => void;
  showOwnCompanyCheckbox?: boolean;
  ownCompanyChecked?: boolean;
  onToggleOwnCompany?: (checked: boolean) => void;
}

const CoordinatorList = ({
  label,
  helpContext,
  list,
  onChange,
  loadingHelp,
  onExplain,
  showOwnCompanyCheckbox,
  ownCompanyChecked,
  onToggleOwnCompany,
}: CoordinatorListProps) => {

  const patch = (index: number, changes: Partial<VorankuendigungCoordinator>) => {
    const next = list.map((entry, i) => (i === index ? { ...entry, ...changes } : entry));
    // Wer den Namen von Hand ändert, löst den Eintrag von den Stammdaten —
    // sonst zöge der Abgleich die Änderung beim nächsten Lauf zurück.
    if (changes.name !== undefined && next[index].coordinatorId) {
      next[index] = { ...next[index], coordinatorId: undefined };
    }
    onChange(next);
  };

  return (
    <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50/50">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <label className="block text-sm font-bold text-gray-800">{label}</label>
          <button
            onClick={() => onExplain(label, helpContext)}
            className="text-blue-600 hover:text-blue-800"
            title="Was bedeutet das?"
          >
            {loadingHelp === label ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
          </button>
        </div>
        {showOwnCompanyCheckbox && (
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={ownCompanyChecked ?? false}
              onChange={(e) => onToggleOwnCompany?.(e.target.checked)}
              className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-[11px] text-blue-600 font-bold uppercase">Eigene Firma</span>
          </label>
        )}
      </div>

      <MultiPersonHint text='Mehrere Personen möglich: Vertreter, 2. SiGeKo usw. — Rolle eintragen und mit „Person hinzufügen" ergänzen. Leer gelassene Einträge erscheinen nicht im Dokument.' />

      <div className="space-y-3">
        {list.map((entry, index) => (
          <EntryCard
            key={entry.id}
            title={index === 0 ? 'Koordinator/in' : `Weitere Person ${index}`}
            onRemove={() => onChange(list.filter((_, i) => i !== index))}
            badge={entry.coordinatorId ? (
              <span className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full border border-green-200 text-[10px] font-bold uppercase">
                <ShieldCheck className="w-3 h-3" /> Verifiziert
              </span>
            ) : undefined}
          >
            <div className="flex flex-col md:flex-row gap-3">
              <div className="w-full md:w-1/2">
                <span className="text-xs text-gray-400 mb-1 block font-semibold uppercase flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Anschrift (Firma)
                </span>
                <AutoTextArea
                  value={entry.company}
                  onChange={(v) => patch(index, { company: v })}
                  minRows={4}
                  placeholder="Firmenanschrift..."
                />
              </div>
              <div className="w-full md:w-1/2 space-y-2">
                <span className="text-xs text-gray-400 block font-semibold uppercase flex items-center gap-1">
                  <User className="w-3 h-3" /> Person
                </span>
                <input
                  type="text"
                  list={ROLE_DATALIST_ID}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  value={entry.role || ''}
                  onChange={(e) => patch(index, { role: e.target.value })}
                  placeholder='Rolle (z. B. "Vertreter", "2. SiGeKo")'
                />
                <TextInput value={entry.name} onChange={(v) => patch(index, { name: v })} placeholder="Name" />
                <TextInput value={entry.phone || ''} onChange={(v) => patch(index, { phone: v })} placeholder="Telefon" />
                <TextInput value={entry.email || ''} onChange={(v) => patch(index, { email: v })} placeholder="E-Mail" />
              </div>
            </div>
          </EntryCard>
        ))}
      </div>

      <div className="mt-3">
        <AddEntryButton
          label="Person hinzufügen"
          onClick={() => onChange([...list, { ...emptyCoordinator(list.length ? 'Vertreter' : undefined), company: list[0]?.company || '' }])}
        />
      </div>
    </div>
  );
};

const AuthorityDropdown = ({ authorities, selectedId, onSelect }: { authorities: Authority[], selectedId: string, onSelect: (id: string) => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedAuth = authorities.find(a => a.id === selectedId);

    const extractCity = (address: string) => {
        if (!address) return '';
        const lines = address.split('\n');
        const lastLine = lines[lines.length - 1];
        const match = lastLine.match(/\d{4,5}\s+(.+)/);
        return match ? match[1] : lastLine;
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full p-2 border border-gray-300 rounded-md text-sm bg-white text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <span className="truncate">
                    {selectedAuth ? `${selectedAuth.name} (${extractCity(selectedAuth.address)})` : '-- Bitte wählen --'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute left-0 top-full mt-1 w-full bg-white rounded-md shadow-xl border border-gray-200 z-50 max-h-60 overflow-y-auto">
                    <div className="grid grid-cols-[1fr_auto] gap-4 p-2 border-b border-gray-100 bg-gray-50 text-[11px] font-bold text-gray-500 uppercase tracking-wider sticky top-0">
                        <div>Behörde</div>
                        <div className="text-right">Ort</div>
                    </div>
                    {authorities.map(auth => (
                        <button
                            key={auth.id}
                            type="button"
                            onClick={() => {
                                onSelect(auth.id);
                                setIsOpen(false);
                            }}
                            className={`w-full text-left grid grid-cols-[1fr_auto] gap-4 p-2 text-sm hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0 ${selectedId === auth.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                            <div className="truncate pr-4">{auth.name}</div>
                            <div className="text-gray-500 whitespace-nowrap text-right">{extractCity(auth.address)}</div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export const FormEditor: React.FC<Props> = ({ data: rawData, onChange, metadata }) => {
  const data = normalizeVorankuendigung(rawData);

  const [addressSearch, setAddressSearch] = useState("");
  const [selectedAuthorityId, setSelectedAuthorityId] = useState("");
  const [authorities, setAuthorities] = useState<Authority[]>([]);

  const [helpContent, setHelpContent] = useState<{ title: string, text: string } | null>(null);
  const [loadingHelp, setLoadingHelp] = useState<string | null>(null);

  useEffect(() => {
    fetchAuthorities().then(setAuthorities).catch(console.error);
  }, []);

  /**
   * Sicherheitsnetz für Altstände, die nicht über die Ansicht normalisiert
   * wurden: die Listen einmal nach oben durchreichen. Danach sind die IDs
   * stabil, `normalizeVorankuendigung` ist ab dann eine Nulloperation.
   */
  useEffect(() => {
    const needsMigration =
      !rawData.clientPersons ||
      !rawData.responsibleThirdParties ||
      !rawData.planningCoordinators ||
      !rawData.executionCoordinators ||
      !rawData.manualFields;
    if (needsMigration) onChange(normalizeVorankuendigung(rawData));
  }, [rawData]);

  const updateField = (field: keyof VorankuendigungData, value: any) => {
    onChange({ ...data, [field]: value });
  };

  /**
   * Punkte 7–10: die Eingabe bleibt in der Vorankündigung. Der Abgleich mit den
   * Projekt-Stammdaten fasst das Feld ab jetzt nicht mehr an; zurück in die
   * Projektdaten schreibt dieses Modul ohnehin nichts.
   */
  const updateManualField = (field: VorankuendigungManualField, value: any) => {
    onChange({ ...data, [field]: value, manualFields: markManualField(data, field) });
  };

  const releaseManualField = (field: VorankuendigungManualField) => {
    onChange({ ...data, manualFields: clearManualField(data, field) });
  };

  const updateEmployer = (index: number, value: string) => {
    const newList = [...(data.employersList || [])];
    while (newList.length <= index) newList.push('');
    newList[index] = value;
    updateManualField('employersList', newList);
  };

  // Function to get Sender Data from Metadata based on type
  const fillSenderFromMetadata = (type: string) => {
      if (!metadata) return;
      let text = '';

      const formatAddr = (addr?: string) => addr ? addr.replace(/,\s*/g, '\n') : '';

      if (type === 'client') {
          text = [
              metadata.clientName,
              formatAddr(metadata.clientAddress),
              metadata.clientContactPerson || null,
              metadata.clientPhone ? `Tel: ${metadata.clientPhone}` : null,
              metadata.clientEmail ? `E-Mail: ${metadata.clientEmail}` : null
          ].filter(Boolean).join('\n');
      } else if (type === 'customer') {
          text = [
              metadata.customerName,
              formatAddr(metadata.customerAddress),
              metadata.customerContactPerson || null,
              metadata.customerPhone ? `Tel: ${metadata.customerPhone}` : null,
              metadata.customerEmail ? `E-Mail: ${metadata.customerEmail}` : null
          ].filter(Boolean).join('\n');
      }

      if (text) updateField('sender', text);
  };

  const handleExplain = async (fieldTitle: string, query: string) => {
    setLoadingHelp(fieldTitle);
    const explanation = await getFieldExplanation(query);
    setHelpContent({ title: fieldTitle, text: explanation });
    setLoadingHelp(null);
  };

  const handleAuthoritySelect = (authId: string) => {
    const auth = authorities.find(a => a.id === authId);
    if (auth) {
      setSelectedAuthorityId(authId);

      // Build full address block with contacts
      let recipientBlock = auth.name + "\n" + auth.address;

      // Append contact info if available
      const contacts = [];
      if (auth.phone) contacts.push(`Tel: ${auth.phone}`);
      if (auth.fax) contacts.push(`Fax: ${auth.fax}`);
      if (auth.email) contacts.push(`E-Mail: ${auth.email}`);

      if (contacts.length > 0) {
          recipientBlock += "\n\n" + contacts.join("\n");
      }

      updateField('recipient', recipientBlock);
    }
  };

  const handleAddressSearch = (query: string) => {
    setAddressSearch(query);
    if (query.length < 3) return;

    // Search in DB catchment area string (comma separated)
    const match = authorities.find(auth =>
      (auth.catchment_area || '').toLowerCase().includes(query.toLowerCase())
    );

    if (match) {
      handleAuthoritySelect(match.id);
    }
  };

  const toggleDangerousWork = (workId: string) => {
    const currentList = data.selectedDangerousWorks || [];
    let newList;
    if (currentList.includes(workId)) {
      newList = currentList.filter(id => id !== workId);
    } else {
      newList = [...currentList, workId];
    }

    const newText = newList
      .map(id => DANGEROUS_WORKS.find(w => w.id === id)?.label)
      .filter(Boolean)
      .join("\n- ");

    const finalText = newList.length > 0 ? "- " + newText : "";

    onChange({
      ...data,
      selectedDangerousWorks: newList,
      dangerousWorkDescription: finalText
    });
  };

  // Punkt 2: natürliche Personen des Bauherrn
  const clientPersons = data.clientPersons || [];
  const patchClientPerson = (index: number, changes: Partial<VorankuendigungPerson>) => {
    updateField('clientPersons', clientPersons.map((p, i) => (i === index ? { ...p, ...changes } : p)));
  };

  // Punkt 4: verantwortliche Dritte
  const parties = data.responsibleThirdParties || [];
  const patchParty = (index: number, changes: Partial<VorankuendigungParty>) => {
    updateField('responsibleThirdParties', parties.map((p, i) => (i === index ? { ...p, ...changes } : p)));
  };

  const employerRows = Math.max(12, (data.employersList || []).length);

  return (
    <div className="relative">
      <RoleDatalist />

      {helpContent && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 animate-fade-in backdrop-blur-sm" onClick={() => setHelpContent(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 relative animate-in fade-in zoom-in duration-200 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setHelpContent(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-4 text-blue-800 sticky top-0 bg-white pt-2">
               <Info className="w-6 h-6" />
               <h3 className="text-lg font-bold">{helpContent.title}</h3>
            </div>
            <div className="prose prose-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {helpContent.text}
            </div>
            <div className="mt-6 text-right">
              <button
                onClick={() => setHelpContent(null)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 font-medium text-sm"
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-md space-y-6">
        <h2 className="text-xl font-bold border-b pb-2 text-blue-800">1. Allgemeine Informationen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputGroup label="Absender (Box links oben)" loadingHelp={loadingHelp} onExplain={handleExplain}>
            <div className="flex justify-between items-center mb-2">
                <label className="text-xs text-slate-500 font-medium uppercase tracking-wider flex items-center gap-1">
                    <ArrowDownCircle className="w-3 h-3"/> Daten übernehmen:
                </label>
                <select
                    className="text-xs border border-slate-300 rounded px-2 py-1 bg-white text-slate-700 focus:ring-1 focus:ring-blue-500 outline-none"
                    onChange={(e) => {
                        if (e.target.value !== 'custom') fillSenderFromMetadata(e.target.value);
                    }}
                    defaultValue="client"
                >
                    <option value="client">Bauherr (Standard)</option>
                    <option value="customer">Auftraggeber</option>
                    <option value="custom">Sonstiges / Freitext</option>
                </select>
            </div>
            <TextArea value={data.sender} onChange={(v) => updateField('sender', v)} minRows={7} />
          </InputGroup>

          <div className="space-y-3">
             <label className="block text-sm font-medium text-gray-700">Empfänger / Behörde auswählen</label>
             <div className="relative">
               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
               </div>
               <input
                  type="text"
                  className="w-full pl-10 p-2 border border-gray-300 rounded-md text-sm bg-gray-50 focus:bg-white transition"
                  placeholder="PLZ oder Ort suchen (z.B. 24113 oder Kiel)..."
                  value={addressSearch}
                  onChange={(e) => handleAddressSearch(e.target.value)}
               />
             </div>
             <AuthorityDropdown
                authorities={authorities}
                selectedId={selectedAuthorityId}
                onSelect={handleAuthoritySelect}
             />
             <div className="mt-2">
               <label className="text-xs text-gray-500 mb-1 block">Vorschau Anschrift (bearbeitbar):</label>
               <TextArea value={data.recipient} onChange={(v) => updateField('recipient', v)} minRows={4} />
             </div>
          </div>
        </div>

        <h2 className="text-xl font-bold border-b pb-2 text-blue-800">2. Projektdetails (Seite 1)</h2>
        <InputGroup label="1. Bezeichnung und Ort der Baustelle" loadingHelp={loadingHelp} onExplain={handleExplain}>
          <TextArea value={data.siteAddress} onChange={(v) => updateField('siteAddress', v)} minRows={2} placeholder="Straße, PLZ, Ort" />
        </InputGroup>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
             <h3 className="font-semibold text-gray-700">2. Bauherr</h3>
             <button
                onClick={() => handleExplain("Bauherr", "Was sind die Pflichten des Bauherrn nach Baustellenverordnung?")}
                className="text-blue-600 hover:text-blue-800"
              >
                {loadingHelp === "Bauherr" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
              </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <TextInput value={data.clientName} onChange={(v) => updateField('clientName', v)} placeholder="Name / Firma" />
            <TextInput value={data.clientAddress} onChange={(v) => updateField('clientAddress', v)} placeholder="Anschrift" />
            <TextInput value={data.clientPhone} onChange={(v) => updateField('clientPhone', v)} placeholder="Telefon" />
            <TextInput value={data.clientEmail} onChange={(v) => updateField('clientEmail', v)} placeholder="E-Mail" />
          </div>

          {/* Natürliche Person(en) — von der Behörde verlangt, wenn der Bauherr eine Firma ist */}
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-gray-800">Natürliche Person(en) des Bauherrn</span>
              <button
                onClick={() => handleExplain("Natürliche Person des Bauherrn", "Warum verlangt die Vorankündigung nach BaustellV neben der Firma des Bauherrn eine natürliche Person mit Telefon und E-Mail?")}
                className="text-blue-600 hover:text-blue-800"
              >
                {loadingHelp === "Natürliche Person des Bauherrn" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
              </button>
            </div>
            <MultiPersonHint text='Ist der Bauherr eine Firma, muss zusätzlich eine natürliche Person mit Telefon und E-Mail benannt werden. Mehrere Personen (z. B. Vertreter) sind möglich.' />

            <div className="space-y-3">
              {clientPersons.map((person, index) => (
                <EntryCard
                  key={person.id}
                  title={index === 0 ? 'Natürliche Person' : `Weitere Person ${index}`}
                  onRemove={() => updateField('clientPersons', clientPersons.filter((_, i) => i !== index))}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      type="text"
                      list={ROLE_DATALIST_ID}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      value={person.role || ''}
                      onChange={(e) => patchClientPerson(index, { role: e.target.value })}
                      placeholder='Rolle (z. B. "Ansprechpartner", "Vertreter")'
                    />
                    <TextInput value={person.name} onChange={(v) => patchClientPerson(index, { name: v })} placeholder="Name" />
                    <TextInput value={person.phone || ''} onChange={(v) => patchClientPerson(index, { phone: v })} placeholder="Telefon" />
                    <TextInput value={person.email || ''} onChange={(v) => patchClientPerson(index, { email: v })} placeholder="E-Mail" />
                  </div>
                </EntryCard>
              ))}
            </div>

            <div className="mt-3 flex items-center gap-2">
              <AddEntryButton
                label="Person hinzufügen"
                onClick={() => updateField('clientPersons', [...clientPersons, emptyPerson(clientPersons.length ? 'Vertreter' : undefined)])}
              />
              {metadata?.clientContactPerson && (
                <button
                  type="button"
                  onClick={() => updateField('clientPersons', [...clientPersons, {
                    ...emptyPerson('Ansprechpartner'),
                    name: metadata.clientContactPerson || '',
                    phone: metadata.clientPhone || '',
                    email: metadata.clientEmail || '',
                  }])}
                  className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1.5 rounded hover:bg-blue-100 transition"
                >
                  <Copy className="w-3 h-3" /> Ansprechpartner aus Projektdaten
                </button>
              )}
            </div>
          </div>
        </div>

        <InputGroup label="3. Art des Bauvorhabens" loadingHelp={loadingHelp} onExplain={handleExplain}>
          <div className="mb-2">
              <select
                className="w-full p-2 border border-gray-300 rounded text-sm bg-white mb-2"
                onChange={(e) => {
                    if(e.target.value) updateField('projectDescription', e.target.value);
                }}
                defaultValue=""
              >
                <option value="" disabled>-- Vorlage auswählen --</option>
                {PROJECT_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
          </div>
          <TextArea value={data.projectDescription} onChange={(v) => updateField('projectDescription', v)} />
        </InputGroup>

        {/* Punkt 4: mehrere verantwortliche Dritte */}
        <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50/50">
          <div className="flex justify-between items-center mb-2">
             <div className="flex items-center gap-2">
                <label className="block text-sm font-bold text-gray-800">4. Verantwortlicher Dritter (§ 4 BaustellV)</label>
                <button
                  onClick={() => handleExplain("Verantwortlicher Dritter", "Was ist ein verantwortlicher Dritter nach § 4 BaustellV und wer kann das sein?")}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {loadingHelp === "Verantwortlicher Dritter" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
                </button>
             </div>
             <button
               onClick={() => updateField('responsibleThirdParties', [...parties, {
                 ...emptyParty(),
                 name: metadata?.clientName || '',
                 address: metadata?.clientAddress || '',
                 phone: metadata?.clientPhone || '',
                 email: metadata?.clientEmail || '',
               }])}
               className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100 transition"
             >
               <Copy className="w-3 h-3" />
               Bauherr übernehmen
             </button>
          </div>

          <MultiPersonHint text='Mehrere Personen möglich: Vertreter, 2. verantwortlicher Dritter usw. — Rolle eintragen und mit „Person hinzufügen" ergänzen.' />

          <div className="space-y-3">
            {parties.map((party, index) => (
              <EntryCard
                key={party.id}
                title={index === 0 ? 'Verantwortlicher Dritter' : `Weitere Person ${index}`}
                onRemove={() => updateField('responsibleThirdParties', parties.filter((_, i) => i !== index))}
              >
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="w-full md:w-1/2">
                    <span className="text-xs text-gray-400 mb-1 block font-semibold uppercase flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Anschrift
                    </span>
                    <AutoTextArea
                      value={party.address || ''}
                      onChange={(v) => patchParty(index, { address: v })}
                      minRows={3}
                      placeholder="Straße, PLZ Ort"
                    />
                  </div>
                  <div className="w-full md:w-1/2 space-y-2">
                    <input
                      type="text"
                      list={ROLE_DATALIST_ID}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      value={party.role || ''}
                      onChange={(e) => patchParty(index, { role: e.target.value })}
                      placeholder='Rolle (z. B. "Vertreter")'
                    />
                    <TextInput value={party.name} onChange={(v) => patchParty(index, { name: v })} placeholder="Name / Firma" />
                    <TextInput value={party.phone || ''} onChange={(v) => patchParty(index, { phone: v })} placeholder="Telefon" />
                    <TextInput value={party.email || ''} onChange={(v) => patchParty(index, { email: v })} placeholder="E-Mail" />
                  </div>
                </div>
              </EntryCard>
            ))}
          </div>

          <div className="mt-3">
            <AddEntryButton
              label="Person hinzufügen"
              onClick={() => updateField('responsibleThirdParties', [...parties, emptyParty()])}
            />
          </div>
        </div>

        <div className="space-y-4">
           <h3 className="font-semibold text-gray-700 text-lg border-b border-gray-200 pb-1 flex items-center gap-2">
             5. Koordinatoren
             <button
                onClick={() => handleExplain("Koordinatoren", "Was sind die Aufgaben des Sicherheits- und Gesundheitsschutzkoordinators (SiGeKo) nach § 3 BaustellV?")}
                className="text-blue-600 hover:text-blue-800"
              >
                {loadingHelp === "Koordinatoren" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
              </button>
           </h3>

           <CoordinatorList
              label="a) für die Planung der Ausführung"
              helpContext="Koordinator für die Planungsphase (SiGeKo)"
              list={data.planningCoordinators || []}
              onChange={(list) => updateField('planningCoordinators', list)}
              loadingHelp={loadingHelp}
              onExplain={handleExplain}
              showOwnCompanyCheckbox={true}
              ownCompanyChecked={data.useOwnCompanyForPlanning}
              onToggleOwnCompany={(checked) => updateField('useOwnCompanyForPlanning', checked)}
           />

           <CoordinatorList
              label="b) für die Ausführung des Bauvorhabens"
              helpContext="Koordinator für die Ausführungsphase (SiGeKo)"
              list={data.executionCoordinators || []}
              onChange={(list) => updateField('executionCoordinators', list)}
              loadingHelp={loadingHelp}
              onExplain={handleExplain}
           />
        </div>

        <h2 className="text-xl font-bold border-b pb-2 text-blue-800">3. Statistik &amp; Zeitplan (Seite 2)</h2>
        <div className="text-xs text-slate-500 flex items-start gap-1 -mt-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Die Punkte 7 bis 10 lassen sich frei überschreiben. Der Wert bleibt dann in der
            Vorankündigung stehen: er wird weder vom Abgleich mit den Projektdaten überschrieben
            noch in die Projektdaten zurückgeschrieben.
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputGroup label="Baubeginn" loadingHelp={loadingHelp} onExplain={handleExplain}>
            <TextInput value={data.startDate} onChange={(v) => updateField('startDate', v)} placeholder="DD.MM.YYYY" />
          </InputGroup>
          <InputGroup label="Bauende" loadingHelp={loadingHelp} onExplain={handleExplain}>
            <TextInput value={data.endDate} onChange={(v) => updateField('endDate', v)} placeholder="DD.MM.YYYY" />
          </InputGroup>
          <InputGroup
            label="7. Max. Beschäftigte"
            loadingHelp={loadingHelp}
            onExplain={handleExplain}
            action={<ManualBadge manual={isManualField(data, 'maxWorkers')} canReset={false} onRelease={() => releaseManualField('maxWorkers')} />}
          >
            <TextArea value={data.maxWorkers} onChange={(v) => updateManualField('maxWorkers', v)} minRows={1} />
          </InputGroup>
          <InputGroup
            label="8. Anzahl Arbeitgebende"
            helpQuery="Was versteht man unter Arbeitgebenden auf einer Baustelle nach BaustellV?"
            loadingHelp={loadingHelp}
            onExplain={handleExplain}
            action={<ManualBadge manual={isManualField(data, 'numEmployers')} canReset={true} onRelease={() => releaseManualField('numEmployers')} />}
          >
            <TextArea value={data.numEmployers} onChange={(v) => updateManualField('numEmployers', v)} minRows={1} />
          </InputGroup>
          <InputGroup
            label="9. Unternehmer ohne Beschäftigte"
            helpQuery="Wer sind Unternehmer ohne Beschäftigte (Solo-Selbstständige) auf der Baustelle?"
            loadingHelp={loadingHelp}
            onExplain={handleExplain}
            action={<ManualBadge manual={isManualField(data, 'numContractors')} canReset={false} onRelease={() => releaseManualField('numContractors')} />}
          >
            <TextArea value={data.numContractors} onChange={(v) => updateManualField('numContractors', v)} minRows={1} />
          </InputGroup>
        </div>

        <InputGroup
          label="10. Liste der Arbeitgebenden"
          loadingHelp={loadingHelp}
          onExplain={handleExplain}
          action={<ManualBadge manual={isManualField(data, 'employersList')} canReset={true} onRelease={() => releaseManualField('employersList')} />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Array.from({ length: employerRows }, (_, i) => (
              <div key={i} className="flex items-start gap-1">
                <span className="text-xs text-gray-400 pt-2 w-6 shrink-0">{i + 1}.</span>
                <AutoTextArea
                  value={(data.employersList || [])[i] || ''}
                  onChange={(v) => updateEmployer(i, v)}
                  minRows={1}
                  placeholder={`Nr. ${i + 1}`}
                  className="!p-1"
                />
              </div>
            ))}
          </div>
          <div className="mt-2">
            <AddEntryButton
              label="Zeile hinzufügen"
              onClick={() => {
                // Auf die angezeigte Zeilenzahl auffüllen, dann eine weitere anhängen.
                const padded = [...(data.employersList || [])];
                while (padded.length < employerRows) padded.push('');
                updateManualField('employersList', [...padded, '']);
              }}
            />
          </div>
        </InputGroup>

        <div className="p-4 bg-red-50 rounded border border-red-200">
          <div className="flex items-center justify-between mb-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.hasDangerousWork}
                onChange={(e) => updateField('hasDangerousWork', e.target.checked)}
                className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
              />
              <span className="font-bold text-gray-800">11. Besonders gefährliche Arbeiten vorhanden?</span>
            </label>
            <button
                onClick={() => handleExplain("Gefährliche Arbeiten", "Was sind besonders gefährliche Arbeiten nach Anhang II der BaustellV?")}
                className="text-blue-600 hover:text-blue-800"
              >
                {loadingHelp === "Gefährliche Arbeiten" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
              </button>
          </div>

          {data.hasDangerousWork && (
            <div className="mt-4 bg-white p-3 rounded border border-gray-200">
               <p className="text-sm font-semibold mb-2 text-gray-700">Bitte auswählen (Mehrfachnennung möglich):</p>
               <div className="space-y-2">
                 {DANGEROUS_WORKS.map(work => (
                   <label key={work.id} className="flex items-start space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        checked={(data.selectedDangerousWorks || []).includes(work.id)}
                        onChange={() => toggleDangerousWork(work.id)}
                      />
                      <span className="text-sm text-gray-600 leading-tight">{work.label}</span>
                   </label>
                 ))}
               </div>

               <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-1">Text für das PDF (wird automatisch generiert, kann manuell angepasst werden):</p>
                  <TextArea value={data.dangerousWorkDescription} onChange={(v) => updateField('dangerousWorkDescription', v)} minRows={3} />
               </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-gray-50 rounded border border-gray-200">
           <div className="flex items-center justify-between mb-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={data.hasSiteManager}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  onChange({ ...data, hasSiteManager: isChecked, siteManager: isChecked ? data.siteManager : "" });
                }}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="font-bold text-gray-800">12. Verantwortliche Bauleitung nach LBO / HBauO vorhanden?</span>
            </label>
            <button
                onClick={() => handleExplain("Verantwortliche Bauleitung", "Was ist die verantwortliche Bauleitung gemäß Landesbauordnung und welche Aufgaben hat sie?")}
                className="text-blue-600 hover:text-blue-800"
              >
                {loadingHelp === "Verantwortliche Bauleitung" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
              </button>
           </div>

           {data.hasSiteManager && (
              <div className="mt-2 pl-8 animate-in fade-in slide-in-from-top-2 duration-200">
                 <TextArea value={data.siteManager} onChange={(v) => updateField('siteManager', v)} minRows={1} placeholder="Name, Telefon" />
              </div>
           )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputGroup label="Unterzeichner Name" loadingHelp={loadingHelp} onExplain={handleExplain}>
            <TextInput value={data.signerName} onChange={(v) => updateField('signerName', v)} />
          </InputGroup>
          <InputGroup label="Ort, Datum" loadingHelp={loadingHelp} onExplain={handleExplain}>
            <TextInput value={data.locationDate} onChange={(v) => updateField('locationDate', v)} />
          </InputGroup>
        </div>
      </div>
    </div>
  );
};
